import { useEffect, useState, useCallback, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import api, { errorMessage } from "../lib/api";
import { PageHeader, ActionButton, GhostButton, PillBadge, FormField, inputClass } from "../components/Primitives";
import { Modal } from "../components/Modal";
import { MultiSelectDropdown } from "../components/MultiSelectDropdown";
import { SingleSelectDropdown } from "../components/SingleSelectDropdown";
import { UserPlus, Trash2, Link2, Copy, Mail, X, Sparkles, Pin, Send, MapPin, ArrowRightLeft, History, LogOut } from "lucide-react";
import { toast } from "sonner";
import { ROLE_LABELS } from "../lib/roles";

const MS_PER_DAY = 86400000;
const MS_PER_HOUR = 3600000;

// iter276 — Initial `role` values use the `builtin:` prefix so they
// round-trip through `splitRoleValue()` at submit time.
const emptyDirect = {
  email: "", password: "", name: "", role: "builtin:supervisor",
  client_name: "", client_id: "", assigned_site_ids: [], manager_id: "",
};
const emptyInvite = {
  email: "", name: "", role: "builtin:supervisor",
  client_name: "", client_id: "", assigned_site_ids: [], manager_id: "",
};

const ROLE_OPTIONS = [
  { value: "supervisor", label: "Supervisor" },
  { value: "operations_manager", label: "Operations Manager" },
  { value: "chief_security_officer", label: "Chief Security Officer" },
  { value: "dispatcher", label: "Dispatcher" },
  { value: "admin", label: "Admin" },
  // Portal-tier roles (new). Legacy `client` still shown for editing existing docs.
  { value: "client_admin", label: "Client Admin (portal)" },
  { value: "regional_manager", label: "Regional Manager (portal)" },
  { value: "site_manager", label: "Site Manager (portal)" },
];

// iter276 — Compose the invite / direct-create role picker options from
// the static built-in list PLUS the agency's custom roles. Callers store
// the compound value (`builtin:<slug>` or `custom:<role_id>`) in form
// state; `splitRoleValue` unpacks it at submit time so the backend
// receives the correct `{role, role_id}` shape.
const roleFormOptions = (availableRoles) => {
  const builtins = ROLE_OPTIONS.map((o) => ({
    value: `builtin:${o.value}`,
    label: o.label,
    base_role: o.value,
    group: "Built-in",
  }));
  const customs = (availableRoles || [])
    .filter((r) => !r.is_builtin && r.is_active !== false && r.base_role !== "super_admin")
    .map((r) => ({
      value: `custom:${r.id}`,
      label: r.display_name,
      base_role: r.base_role,
      group: "Custom (agency)",
    }));
  return [...builtins, ...customs];
};

// Unpack a compound role value into a payload the backend accepts. The
// `role` field is always the built-in base slug so portal-tier scope
// checks + legacy role-name comparisons behave correctly regardless of
// whether a custom role was picked.
const splitRoleValue = (compound, availableRoles) => {
  if (compound?.startsWith("custom:")) {
    const id = compound.slice("custom:".length);
    const found = (availableRoles || []).find((r) => r.id === id);
    return { role: found?.base_role || "supervisor", role_id: id };
  }
  const raw = compound?.startsWith("builtin:") ? compound.slice("builtin:".length) : compound;
  return { role: raw || "supervisor", role_id: "" };
};

// Effective built-in slug — used by the form-side portal-tier gating
// (`CLIENT_TIER.has(...)` etc). Custom roles inherit their base_role.
const effectiveBaseRole = (compound, availableRoles) =>
  splitRoleValue(compound, availableRoles).role;
const REPORTS_TO_ROLES = new Set(["supervisor", "dispatcher"]);
const CLIENT_TIER = new Set(["client", "client_admin", "regional_manager", "site_manager"]);
const ROLE_TONE = {
  admin: "red",
  chief_security_officer: "red",
  operations_manager: "amber",
  supervisor: "blue",
  dispatcher: "blue",
  client: "amber",
  client_admin: "amber",
  regional_manager: "amber",
  site_manager: "amber",
};
const ROLE_LABEL = { ...ROLE_LABELS };
const roleTone = (r) => ROLE_TONE[r] || "default";

export default function Users() {
  const { user: currentUser } = useAuth();
  const canWrite = currentUser?.role === "admin" || currentUser?.role === "super_admin";
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [clients, setClients] = useState([]);
  const [sites, setSites] = useState([]);
  // iter184 — dynamic roles from the /api/roles endpoint. The Role cell on
  // each user row renders as an inline dropdown listing built-ins + this
  // agency's custom roles, so admins can assign either.
  const [availableRoles, setAvailableRoles] = useState([]);
  const [mode, setMode] = useState(null); // "direct" | "invite" | null
  const [direct, setDirect] = useState(emptyDirect);
  const [invForm, setInvForm] = useState(emptyInvite);
  const [generated, setGenerated] = useState(null); // {token, url, email, role, expires_at}

  const load = useCallback(async () => {
    try {
      const u = await api.get("/users");
      setUsers(u.data);
    } catch { setUsers([]); }
    if (canWrite) {
      try {
        const i = await api.get("/users/invites");
        setInvites(i.data);
      } catch { setInvites([]); }
      try {
        const r = await api.get("/roles");
        setAvailableRoles(r.data || []);
      } catch { setAvailableRoles([]); }
      // Load clients + sites for the portal-tier pickers.
      try {
        const c = await api.get("/clients");
        setClients(Array.isArray(c.data) ? c.data : (c.data?.items || []));
      } catch { setClients([]); }
      try {
        const s = await api.get("/sites");
        setSites(Array.isArray(s.data) ? s.data : (s.data?.items || []));
      } catch { setSites([]); }
    } else {
      setInvites([]);
    }
  }, [canWrite]);
  useEffect(() => { load(); }, [load]);

  // Sites belonging to the currently-selected client (for portal user scoping).
  const sitesForClient = useMemo(() => {
    const cid = mode === "direct" ? direct.client_id : invForm.client_id;
    if (!cid) return [];
    return sites.filter((s) => s.client_id === cid);
  }, [sites, mode, direct.client_id, invForm.client_id]);

  const validatePortal = (form) => {
    const effective = effectiveBaseRole(form.role, availableRoles);
    if (!CLIENT_TIER.has(effective)) return null;
    if (effective === "client" && !form.client_name) return "Client name required (legacy)";
    if (effective !== "client" && !form.client_id) return "Client is required for portal users";
    if (effective === "regional_manager" && (!form.assigned_site_ids || form.assigned_site_ids.length === 0)) {
      return "Regional Manager needs at least one assigned site";
    }
    if (effective === "site_manager" && (!form.assigned_site_ids || form.assigned_site_ids.length === 0)) {
      return "Site Manager needs at least one assigned site";
    }
    return null;
  };

  // iter276 — Compose the create-time payload. `role` is always the
  // built-in base slug; `role_id` is set only when a custom role was
  // picked from the merged dropdown.
  const buildCreatePayload = (form) => {
    const { role, role_id } = splitRoleValue(form.role, availableRoles);
    const p = { ...form, role, role_id };
    if (!role_id) delete p.role_id;
    if (!CLIENT_TIER.has(role)) {
      delete p.client_id; delete p.assigned_site_ids; delete p.client_name;
    }
    return p;
  };

  const saveDirect = async () => {
    if (!direct.email || !direct.password || !direct.name) { toast.error("All fields required"); return; }
    const err = validatePortal(direct);
    if (err) { toast.error(err); return; }
    try {
      await api.post("/auth/register", buildCreatePayload(direct));
      toast.success("User created");
      setMode(null); setDirect(emptyDirect); load();
    } catch (e) {
      toast.error(errorMessage(e, "Failed"));
    }
  };

  const createInvite = async () => {
    if (!invForm.email || !invForm.name) { toast.error("Email and name required"); return; }
    const err = validatePortal(invForm);
    if (err) { toast.error(err); return; }
    try {
      const res = await api.post("/users/invite", buildCreatePayload(invForm));
      const url = `${window.location.origin}${res.data.accept_path}`;
      setGenerated({ ...res.data, url });
      if (res.data.email_sent) {
        toast.success(`Invite emailed to ${res.data.email}`);
      } else {
        toast.success("Invite link generated (email delivery unavailable — copy manually)");
      }
      setInvForm(emptyInvite);
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Failed"));
    }
  };

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Invite link copied");
    } catch {
      toast.error("Copy failed — select & copy manually");
    }
  };

  const revokeInvite = async (token) => {
    if (!window.confirm("Revoke this invite link?")) return;
    await api.delete(`/users/invites/${token}`);
    toast.success("Invite revoked");
    load();
  };

  const resendInvite = async (inv) => {
    if (!window.confirm(`Resend the activation link to ${inv.email}? The 7-day expiry is preserved.`)) return;
    try {
      const res = await api.post(`/users/invites/${inv.token}/resend`);
      if (res.data.email_sent) {
        toast.success(`Invite re-emailed to ${res.data.email}`);
      } else {
        toast.error(`Resend failed — ${res.data.email_reason || "email disabled"}. Copy the link and share it manually.`);
      }
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Resend failed"));
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete user?")) return;
    await api.delete(`/users/${id}`); toast.success("Removed"); load();
  };

  const onTerminateSessions = async (u) => {
    // iter295 — "Sign out everywhere". Backend stamps
    // session_invalidate_before → every live JWT for this user is
    // rejected on its next request across every device / tab.
    if (!window.confirm(`Sign ${u.name || u.email} out of every device? Their current sessions will be invalidated immediately.`)) return;
    try {
      await api.post(`/users/${u.id}/terminate-sessions`);
      toast.success(`${u.name || u.email} signed out everywhere.`);
    } catch (e) {
      toast.error(errorMessage(e, "Sign-out failed"));
    }
  };

  const onCopyLayout = async (u) => {
    if (!window.confirm(`Copy ${u.name || u.email}'s sidebar layout onto your account? This replaces your current layout.`)) return;
    try {
      const res = await api.post(`/users/me/preferences/copy-from/${u.id}`);
      toast.success(`Layout copied from ${res.data?.source_name || u.name || u.email}`);
      // Tell the Layout to re-hydrate without a full reload.
      window.dispatchEvent(new CustomEvent("irisguard:prefs-updated"));
    } catch (e) {
      toast.error(errorMessage(e, "Copy failed"));
    }
  };

  const onPinAsAgencyDefault = async (u) => {
    if (!window.confirm(`Pin ${u.name || u.email}'s layout as the agency default? New invited and registered users will start with this layout.`)) return;
    try {
      const res = await api.post(`/agencies/default-prefs/from/${u.id}`);
      toast.success(`Agency default layout pinned from ${res.data?.source_name || u.name || u.email}`);
    } catch (e) {
      toast.error(errorMessage(e, "Pin failed"));
    }
  };

  const tone = (r) => ROLE_TONE[r] || "default";
  const roleLabel = (r) => ROLE_LABEL[r] || r;
  const opsManagers = users.filter((u) => u.role === "operations_manager" && u.is_active !== false);

  // iter239 — Users page filters. Multi-select searchable dropdowns on
  // Username / Name / Email / Role. Client-side because agencies rarely
  // hit 200 users in practice.
  const [userFilters, setUserFilters] = useState({
    usernames: [], names: [], emails: [], roles: [],
  });
  const userFilterOptions = useMemo(() => {
    const names = new Set(), emails = new Set(), unames = new Set(), roles = new Set();
    for (const u of users) {
      if (u.name) names.add(u.name);
      if (u.email) emails.add(u.email);
      if (u.username) unames.add(u.username);
      if (u.role) roles.add(u.role);
    }
    const toOpts = (set) => Array.from(set).sort().map((v) => ({ value: v, label: v }));
    return {
      names:     toOpts(names),
      emails:    toOpts(emails),
      usernames: toOpts(unames),
      roles:     Array.from(roles).sort().map((r) => ({ value: r, label: roleLabel(r) })),
    };
  }, [users]);
  const filteredUsers = useMemo(() => {
    const { usernames, names, emails, roles } = userFilters;
    if (!usernames.length && !names.length && !emails.length && !roles.length) return users;
    return users.filter((u) => (
      (!usernames.length || usernames.includes(u.username)) &&
      (!names.length     || names.includes(u.name)) &&
      (!emails.length    || emails.includes(u.email)) &&
      (!roles.length     || roles.includes(u.role))
    ));
  }, [users, userFilters]);
  const anyUserFilter = userFilters.usernames.length + userFilters.names.length
                       + userFilters.emails.length + userFilters.roles.length > 0;

  const expiresIn = (iso) => {
    const ms = new Date(iso).getTime() - Date.now();
    if (ms <= 0) return "expired";
    const d = Math.floor(ms / MS_PER_DAY);
    return d > 0 ? `${d}d left` : `${Math.floor(ms / MS_PER_HOUR)}h left`;
  };

  // iter276 — derived built-in slug from the (possibly compound) form
  // value. Portal-tier gating, client_name visibility, and the
  // reports-to picker all key off the built-in base.
  const directBase = effectiveBaseRole(direct.role, availableRoles);
  const invBase = effectiveBaseRole(invForm.role, availableRoles);
  const roleOpts = roleFormOptions(availableRoles);

  return (
    <div>
      <PageHeader
        title="Users"
        subtitle={canWrite
          ? "Manage operators and client portal accounts. Generate invite links so new operators set their own password."
          : "Read-only view. Only admins can create, invite, or remove user accounts."}
        action={canWrite ? (
          <div className="flex items-center gap-2">
            <GhostButton data-testid="invite-link-button" onClick={() => { setInvForm(emptyInvite); setGenerated(null); setMode("invite"); }}>
              <Link2 className="size-4" /> Generate Invite Link
            </GhostButton>
            <ActionButton data-testid="add-user-button" onClick={() => { setDirect(emptyDirect); setMode("direct"); }}>
              <UserPlus className="size-4" /> Create Directly
            </ActionButton>
          </div>
        ) : null}
      />

      <div className="p-4 md:p-8 space-y-6">
        {/* Pending invites */}
        {invites.length > 0 && (
          <div className="border border-[#27272A] bg-[#121212]" data-testid="pending-invites-section">
            <div className="px-5 py-4 border-b border-[#27272A] flex items-center justify-between">
              <div>
                <div className="label-mute">Pending</div>
                <div className="font-display text-xl uppercase font-bold tracking-tight">Open Invite Links</div>
              </div>
              <PillBadge tone="amber">{invites.length} pending</PillBadge>
            </div>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm" data-testid="invites-table">
                <thead className="border-b border-[#27272A] text-left">
                  <tr className="label-mute">
                    <th className="px-4 py-3">Invitee</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Role</th>
                    <th className="px-4 py-3">Expires</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#27272A]">
                  {invites.map((inv) => {
                    const url = `${window.location.origin}/accept-invite/${inv.token}`;
                    return (
                      <tr key={inv.token} className="hover:bg-[#1c1c1e]">
                        <td className="px-4 py-3 font-medium">{inv.name}</td>
                        <td className="px-4 py-3 font-mono text-zinc-400">
                          {inv.email}
                          {inv.last_resent_at && (
                            <div className="text-[10px] text-[#0A84FF] mt-0.5 uppercase tracking-widest" data-testid={`last-resent-${inv.token}`}>
                              re-sent {new Date(inv.last_resent_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3"><PillBadge tone={tone(inv.role)}>{roleLabel(inv.role)}</PillBadge></td>
                        <td className="px-4 py-3 font-mono text-xs text-[#FF9F0A]">{expiresIn(inv.expires_at)}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex items-center gap-1">
                            {canWrite && (
                              <GhostButton
                                data-testid={`resend-invite-${inv.token}`}
                                onClick={() => resendInvite(inv)}
                                title="Resend the activation email"
                              >
                                <Send className="size-3.5" />
                              </GhostButton>
                            )}
                            <GhostButton data-testid={`copy-invite-${inv.token}`} onClick={() => copyLink(url)} title="Copy invite link">
                              <Copy className="size-3.5" />
                            </GhostButton>
                            {canWrite && (
                              <GhostButton data-testid={`revoke-invite-${inv.token}`} onClick={() => revokeInvite(inv.token)} title="Revoke invite">
                                <X className="size-3.5" />
                              </GhostButton>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile — invites card stack */}
            <div className="md:hidden divide-y divide-[#27272A]" data-testid="invites-mobile-list">
              {invites.map((inv) => {
                const url = `${window.location.origin}/accept-invite/${inv.token}`;
                return (
                  <div key={inv.token} className="p-3 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-white truncate">{inv.name}</div>
                        <div className="font-mono text-zinc-400 text-xs break-all">{inv.email}</div>
                        {inv.last_resent_at && (
                          <div
                            className="text-[10px] text-[#0A84FF] mt-0.5 uppercase tracking-widest"
                            data-testid={`last-resent-mobile-${inv.token}`}
                          >
                            re-sent {new Date(inv.last_resent_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                          </div>
                        )}
                      </div>
                      <PillBadge tone={tone(inv.role)}>{roleLabel(inv.role)}</PillBadge>
                    </div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#FF9F0A]">
                      Expires · {expiresIn(inv.expires_at)}
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      {canWrite && (
                        <GhostButton
                          data-testid={`resend-invite-mobile-${inv.token}`}
                          onClick={() => resendInvite(inv)}
                          title="Resend the activation email"
                        >
                          <Send className="size-3.5" /> Resend
                        </GhostButton>
                      )}
                      <GhostButton data-testid={`copy-invite-mobile-${inv.token}`} onClick={() => copyLink(url)} title="Copy invite link">
                        <Copy className="size-3.5" /> Copy
                      </GhostButton>
                      {canWrite && (
                        <GhostButton data-testid={`revoke-invite-mobile-${inv.token}`} onClick={() => revokeInvite(inv.token)} title="Revoke invite">
                          <X className="size-3.5" /> Revoke
                        </GhostButton>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Active users */}
        <div className="border border-[#27272A] bg-[#121212]">
          <div className="px-5 py-4 border-b border-[#27272A] flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="label-mute">Roster</div>
              <div className="font-display text-xl uppercase font-bold tracking-tight">Active Accounts</div>
            </div>
            <div className="flex items-center gap-2 flex-wrap" data-testid="users-filters-row">
              <MultiSelectDropdown
                label="Username"
                options={userFilterOptions.usernames}
                selected={userFilters.usernames}
                onChange={(next) => setUserFilters((f) => ({ ...f, usernames: next }))}
                searchable
                testId="users-filter-username"
              />
              <MultiSelectDropdown
                label="Name"
                options={userFilterOptions.names}
                selected={userFilters.names}
                onChange={(next) => setUserFilters((f) => ({ ...f, names: next }))}
                searchable
                testId="users-filter-name"
              />
              <MultiSelectDropdown
                label="Email"
                options={userFilterOptions.emails}
                selected={userFilters.emails}
                onChange={(next) => setUserFilters((f) => ({ ...f, emails: next }))}
                searchable
                testId="users-filter-email"
              />
              <MultiSelectDropdown
                label="Role"
                options={userFilterOptions.roles}
                selected={userFilters.roles}
                onChange={(next) => setUserFilters((f) => ({ ...f, roles: next }))}
                searchable
                testId="users-filter-role"
              />
              {anyUserFilter && (
                <GhostButton
                  data-testid="users-filter-clear"
                  onClick={() => setUserFilters({ usernames: [], names: [], emails: [], roles: [] })}
                  title="Clear all filters"
                >
                  <X className="size-3.5" /> Clear
                </GhostButton>
              )}
              <div className="text-[10px] uppercase tracking-widest text-zinc-500" data-testid="users-filter-count">
                {filteredUsers.length} of {users.length}
              </div>
            </div>
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm" data-testid="users-table">
            <thead className="border-b border-[#27272A] text-left">
              <tr className="label-mute">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Client</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#27272A]">
              {filteredUsers.map((u) => (
                <tr key={u.id} className="hover:bg-[#1c1c1e]">
                  <td className="px-4 py-3 font-medium">
                    <div>{u.name}</div>
                    {u.username && (
                      <div
                        className="text-[10px] font-mono text-[#34C759]/80 mt-0.5"
                        data-testid={`user-username-${u.id}`}
                        title="Username used to log in (in addition to email)"
                      >
                        @{u.username}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-zinc-400">{u.email}</td>
                  <td className="px-4 py-3">
                    <RoleCell
                      user={u}
                      currentUserId={currentUser?.id}
                      roles={availableRoles}
                      canWrite={canWrite}
                      onChanged={load}
                    />
                  </td>
                  <td className="px-4 py-3 text-zinc-400">{u.client_name || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-500">{new Date(u.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <ManageSitesButton
                        user={u}
                        allSites={sites}
                        allUsers={users}
                        canWrite={canWrite}
                        onChanged={load}
                      />
                      <GhostButton
                        data-testid={`copy-layout-${u.id}`}
                        onClick={() => onCopyLayout(u)}
                        title={`Apply ${u.name || u.email}'s sidebar layout to your account`}
                      >
                        <Sparkles className="size-3.5" />
                      </GhostButton>
                      {canWrite && (
                        <GhostButton
                          data-testid={`pin-agency-default-${u.id}`}
                          onClick={() => onPinAsAgencyDefault(u)}
                          title={`Pin ${u.name || u.email}'s layout as the agency default for new accounts`}
                        >
                          <Pin className="size-3.5" />
                        </GhostButton>
                      )}
                      {canWrite && (
                        <GhostButton
                          data-testid={`terminate-sessions-${u.id}`}
                          onClick={() => onTerminateSessions(u)}
                          title={`Sign ${u.name || u.email} out of every device — invalidates every live JWT`}
                        >
                          <LogOut className="size-3.5" />
                        </GhostButton>
                      )}
                      {canWrite && (
                        <GhostButton data-testid={`delete-user-${u.id}`} onClick={() => onDelete(u.id)}><Trash2 className="size-3.5" /></GhostButton>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          {/* Mobile — active users card stack */}
          <div className="md:hidden divide-y divide-[#27272A]" data-testid="users-mobile-list">
            {filteredUsers.map((u) => (
              <div key={u.id} className="p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-white break-words">{u.name}</div>
                    {u.username && (
                      <div
                        className="text-[10px] font-mono text-[#34C759]/80 mt-0.5"
                        data-testid={`user-username-mobile-${u.id}`}
                        title="Username used to log in (in addition to email)"
                      >
                        @{u.username}
                      </div>
                    )}
                    <div className="font-mono text-xs text-zinc-400 break-all mt-0.5">{u.email}</div>
                  </div>
                  <div className="shrink-0">
                    <RoleCell
                      user={u}
                      currentUserId={currentUser?.id}
                      roles={availableRoles}
                      canWrite={canWrite}
                      onChanged={load}
                    />
                  </div>
                </div>
                <div className="text-xs text-zinc-400 space-y-0.5">
                  {u.client_name && (
                    <div><span className="text-zinc-600 uppercase tracking-widest text-[9px] font-mono">Client · </span>{u.client_name}</div>
                  )}
                  <div><span className="text-zinc-600 uppercase tracking-widest text-[9px] font-mono">Created · </span><span className="font-mono">{new Date(u.created_at).toLocaleDateString()}</span></div>
                </div>
                <div className="flex flex-wrap items-center gap-1.5 pt-1 border-t border-[#1f1f22]">
                  <ManageSitesButton
                    user={u}
                    allSites={sites}
                    allUsers={users}
                    canWrite={canWrite}
                    onChanged={load}
                  />
                  <GhostButton
                    data-testid={`copy-layout-mobile-${u.id}`}
                    onClick={() => onCopyLayout(u)}
                    title={`Apply ${u.name || u.email}'s sidebar layout to your account`}
                  >
                    <Sparkles className="size-3.5" />
                  </GhostButton>
                  {canWrite && (
                    <GhostButton
                      data-testid={`pin-agency-default-mobile-${u.id}`}
                      onClick={() => onPinAsAgencyDefault(u)}
                      title={`Pin ${u.name || u.email}'s layout as the agency default for new accounts`}
                    >
                      <Pin className="size-3.5" />
                    </GhostButton>
                  )}
                  {canWrite && (
                    <GhostButton
                      data-testid={`terminate-sessions-mobile-${u.id}`}
                      onClick={() => onTerminateSessions(u)}
                      title={`Sign ${u.name || u.email} out of every device`}
                    >
                      <LogOut className="size-3.5" />
                    </GhostButton>
                  )}
                  {canWrite && (
                    <GhostButton data-testid={`delete-user-mobile-${u.id}`} onClick={() => onDelete(u.id)}><Trash2 className="size-3.5" /></GhostButton>
                  )}
                </div>
              </div>
            ))}
            {filteredUsers.length === 0 && (
              <div className="p-8 text-center text-zinc-500 scanlines">No users match the current filters.</div>
            )}
          </div>
        </div>
      </div>

      {/* Direct create modal */}
      {mode === "direct" && (
        <Modal title="Create User Directly" onClose={() => setMode(null)} testId="user-form-modal">
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Full Name"><input data-testid="user-name-input" className={inputClass} value={direct.name} onChange={(e) => setDirect({ ...direct, name: e.target.value })} /></FormField>
            <FormField label="Email"><input data-testid="user-email-input" type="email" className={inputClass} value={direct.email} onChange={(e) => setDirect({ ...direct, email: e.target.value })} /></FormField>
            <FormField label="Password"><input data-testid="user-password-input" type="text" className={inputClass} value={direct.password} onChange={(e) => setDirect({ ...direct, password: e.target.value })} /></FormField>
            <FormField label="Role">
              <select
                data-testid="user-role-select"
                className={inputClass}
                value={direct.role}
                onChange={(e) => {
                  const nextBase = effectiveBaseRole(e.target.value, availableRoles);
                  setDirect({
                    ...direct,
                    role: e.target.value,
                    manager_id: REPORTS_TO_ROLES.has(nextBase) ? direct.manager_id : "",
                  });
                }}
              >
                <optgroup label="Built-in">
                  {roleOpts.filter((o) => o.group === "Built-in").map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </optgroup>
                {roleOpts.some((o) => o.group === "Custom (agency)") && (
                  <optgroup label="Custom (agency)">
                    {roleOpts.filter((o) => o.group === "Custom (agency)").map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </optgroup>
                )}
              </select>
            </FormField>
            {directBase === "client" && (
              <div className="col-span-2">
                <FormField label="Client Name (legacy — must match Site client_name)">
                  <input data-testid="user-client-input" className={inputClass} value={direct.client_name} onChange={(e) => setDirect({ ...direct, client_name: e.target.value })} />
                </FormField>
              </div>
            )}
            {CLIENT_TIER.has(directBase) && directBase !== "client" && (
              <div className="col-span-2 space-y-3 border border-[#27272A] p-3 bg-[#0A0A0A]" data-testid="portal-user-scope">
                <FormField label="Client *">
                  <SingleSelectDropdown
                    testId="user-client-picker"
                    label="Pick a client"
                    options={clients.map((c) => ({ value: c.id, label: c.company_name || c.name || "Unnamed" }))}
                    value={direct.client_id}
                    onChange={(v) => setDirect({ ...direct, client_id: v, assigned_site_ids: [] })}
                    searchable
                    triggerClassName="w-full"
                  />
                </FormField>
                {directBase === "regional_manager" && (
                  <FormField label="Assigned Sites * (≥1)">
                    <MultiSelectDropdown
                      testId="user-sites-multi"
                      label={sitesForClient.length ? `${sitesForClient.length} sites available` : "Pick a client first"}
                      options={sitesForClient.map((s) => ({ value: s.id, label: s.name }))}
                      selected={direct.assigned_site_ids || []}
                      onChange={(v) => setDirect({ ...direct, assigned_site_ids: v })}
                      searchable
                      triggerClassName="w-full"
                    />
                  </FormField>
                )}
                {directBase === "site_manager" && (
                  <FormField label="Assigned Sites * (≥1)">
                    <MultiSelectDropdown
                      testId="user-site-manager-multi"
                      label={sitesForClient.length ? `${sitesForClient.length} sites available` : "Pick a client first"}
                      options={sitesForClient.map((s) => ({ value: s.id, label: s.name }))}
                      selected={direct.assigned_site_ids || []}
                      onChange={(v) => setDirect({ ...direct, assigned_site_ids: v })}
                      searchable
                      triggerClassName="w-full"
                    />
                  </FormField>
                )}
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                  Portal users can only view; they never appear on the mobile app.
                </div>
              </div>
            )}
            {REPORTS_TO_ROLES.has(directBase) && opsManagers.length > 0 && (
              <FormField label="Reports to (Operations Manager)">
                <select data-testid="user-manager-select" className={inputClass} value={direct.manager_id || ""} onChange={(e) => setDirect({ ...direct, manager_id: e.target.value })}>
                  <option value="">— none —</option>
                  {opsManagers.map((m) => <option key={m.id} value={m.id}>{m.name} · {m.email}</option>)}
                </select>
              </FormField>
            )}
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <GhostButton onClick={() => setMode(null)}>Cancel</GhostButton>
            <ActionButton data-testid="save-user-button" onClick={saveDirect}>Create</ActionButton>
          </div>
        </Modal>
      )}

      {/* Invite link modal */}
      {mode === "invite" && (
        <Modal title="Generate Invite Link" onClose={() => { setMode(null); setGenerated(null); }} testId="invite-form-modal">
          {!generated ? (
            <>
              <p className="text-xs text-zinc-500 mb-4 leading-relaxed">
                Invitee receives a one-time link valid for 7 days. They set their own password on activation — no shared credentials.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Full Name"><input data-testid="invite-name-input" className={inputClass} value={invForm.name} onChange={(e) => setInvForm({ ...invForm, name: e.target.value })} /></FormField>
                <FormField label="Email"><input data-testid="invite-email-input" type="email" className={inputClass} value={invForm.email} onChange={(e) => setInvForm({ ...invForm, email: e.target.value })} /></FormField>
                <FormField label="Role">
                  <select
                    data-testid="invite-role-select"
                    className={inputClass}
                    value={invForm.role}
                    onChange={(e) => {
                      const nextBase = effectiveBaseRole(e.target.value, availableRoles);
                      setInvForm({
                        ...invForm,
                        role: e.target.value,
                        manager_id: REPORTS_TO_ROLES.has(nextBase) ? invForm.manager_id : "",
                      });
                    }}
                  >
                    <optgroup label="Built-in">
                      {roleOpts.filter((o) => o.group === "Built-in").map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </optgroup>
                    {roleOpts.some((o) => o.group === "Custom (agency)") && (
                      <optgroup label="Custom (agency)">
                        {roleOpts.filter((o) => o.group === "Custom (agency)").map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </FormField>
                {invBase === "client" && (
                  <FormField label="Client Name (legacy)">
                    <input data-testid="invite-client-input" className={inputClass} value={invForm.client_name} onChange={(e) => setInvForm({ ...invForm, client_name: e.target.value })} />
                  </FormField>
                )}
                {CLIENT_TIER.has(invBase) && invBase !== "client" && (
                  <div className="col-span-2 space-y-3 border border-[#27272A] p-3 bg-[#0A0A0A]" data-testid="portal-invite-scope">
                    <FormField label="Client *">
                      <SingleSelectDropdown
                        testId="invite-client-picker"
                        label="Pick a client"
                        options={clients.map((c) => ({ value: c.id, label: c.company_name || c.name || "Unnamed" }))}
                        value={invForm.client_id}
                        onChange={(v) => setInvForm({ ...invForm, client_id: v, assigned_site_ids: [] })}
                        searchable
                        triggerClassName="w-full"
                      />
                    </FormField>
                    {invBase === "regional_manager" && (
                      <FormField label="Assigned Sites * (≥1)">
                        <MultiSelectDropdown
                          testId="invite-sites-multi"
                          label={sitesForClient.length ? `${sitesForClient.length} sites available` : "Pick a client first"}
                          options={sitesForClient.map((s) => ({ value: s.id, label: s.name }))}
                          selected={invForm.assigned_site_ids || []}
                          onChange={(v) => setInvForm({ ...invForm, assigned_site_ids: v })}
                          searchable
                          triggerClassName="w-full"
                        />
                      </FormField>
                    )}
                    {invBase === "site_manager" && (
                      <FormField label="Assigned Sites * (≥1)">
                        <MultiSelectDropdown
                          testId="invite-site-manager-multi"
                          label={sitesForClient.length ? `${sitesForClient.length} sites available` : "Pick a client first"}
                          options={sitesForClient.map((s) => ({ value: s.id, label: s.name }))}
                          selected={invForm.assigned_site_ids || []}
                          onChange={(v) => setInvForm({ ...invForm, assigned_site_ids: v })}
                          searchable
                          triggerClassName="w-full"
                        />
                      </FormField>
                    )}
                  </div>
                )}
                {REPORTS_TO_ROLES.has(invBase) && opsManagers.length > 0 && (
                  <FormField label="Reports to (Operations Manager)">
                    <select data-testid="invite-manager-select" className={inputClass} value={invForm.manager_id || ""} onChange={(e) => setInvForm({ ...invForm, manager_id: e.target.value })}>
                      <option value="">— none —</option>
                      {opsManagers.map((m) => <option key={m.id} value={m.id}>{m.name} · {m.email}</option>)}
                    </select>
                  </FormField>
                )}
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <GhostButton onClick={() => setMode(null)}>Cancel</GhostButton>
                <ActionButton data-testid="generate-invite-button" onClick={createInvite}>
                  <Link2 className="size-4" /> Generate Link
                </ActionButton>
              </div>
            </>
          ) : (
            <div data-testid="invite-link-result">
              {generated.email_sent ? (
                <div className="border border-[#34C759]/30 bg-[#34C759]/5 p-4 mb-4">
                  <div className="label-mute text-[#34C759] mb-1">Invite email sent</div>
                  <div className="text-sm">
                    An activation link was emailed to <span className="font-mono text-white">{generated.email}</span>. It expires in 7 days and works only once. If they don&apos;t see it, check spam or share the link below manually.
                  </div>
                </div>
              ) : (
                <div className="border border-[#FF9F0A]/40 bg-[#FF9F0A]/5 p-4 mb-4">
                  <div className="label-mute text-[#FF9F0A] mb-1">Invite ready — email not delivered</div>
                  <div className="text-sm">
                    Automated delivery is unavailable ({generated.email_reason || "email disabled"}). Send this link to <span className="font-mono text-white">{generated.email}</span> manually. It expires in 7 days and works only once.
                  </div>
                </div>
              )}
              <FormField label="One-time Activation Link">
                <input
                  data-testid="invite-link-input"
                  className={inputClass}
                  readOnly
                  value={generated.url}
                  onFocus={(e) => e.target.select()}
                />
              </FormField>
              <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
                <GhostButton onClick={() => { setGenerated(null); }}>Generate Another</GhostButton>
                <a
                  href={`mailto:${generated.email}?subject=Iris%20Guard%20—%20Activate%20your%20account&body=${encodeURIComponent(
                    `Hi ${generated.name || ""},\n\nYou've been invited to Iris Security as ${generated.role}.\nActivate your account here (link valid 7 days):\n\n${generated.url}\n`
                  )}`}
                  className="inline-flex items-center gap-2 border border-[#27272A] hover:border-[#0A84FF] hover:text-[#0A84FF] text-zinc-300 px-4 py-2 text-xs uppercase tracking-widest transition-colors"
                  data-testid="email-invite-button"
                >
                  <Mail className="size-4" /> Email Invitee
                </a>
                <ActionButton data-testid="copy-invite-link-button" onClick={() => copyLink(generated.url)}>
                  <Copy className="size-4" /> Copy Link
                </ActionButton>
              </div>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}


/**
 * Inline "Manage Sites" action for portal-tier managers.
 *
 * Site Manager and Regional Manager users both store their scope in
 * `assigned_site_ids`. Previously that list was create-time-only — moving
 * a lead to a new site required deleting and recreating the account. This
 * button opens a small modal with the multi-picker pre-loaded with the
 * user's current sites and PATCHes /api/users/{id} on save. Only admin /
 * super_admin can write; everyone else sees a static site count pill.
 */
function ManageSitesButton({ user, allSites, allUsers, canWrite, onChanged }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(user.assigned_site_ids || []);
  const [busy, setBusy] = useState(false);
  // Tab state — the modal now hosts three sections:
  //   'edit'     — the classic multi-picker (default)
  //   'reassign' — bulk transfer of all this user's sites to another lead
  //   'history'  — audit-trail slice for this user's site scope
  const [tab, setTab] = useState("edit");
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  // Reassignment form state — separated from the picker to avoid a
  // half-saved picker leaking into a bulk-transfer flow.
  const [reassignTargetId, setReassignTargetId] = useState("");
  const [reassignMerge, setReassignMerge] = useState(true);
  const [reassignDeactivate, setReassignDeactivate] = useState(true);

  const isPortalManager = user.role === "regional_manager" || user.role === "site_manager";

  // Sites the picker should show — filtered to the user's client so admins
  // can't accidentally assign a site that doesn't belong to their client.
  const options = useMemo(
    () => (allSites || [])
      .filter((s) => !user.client_id || s.client_id === user.client_id)
      .map((s) => ({ value: s.id, label: s.name })),
    [allSites, user.client_id],
  );

  // Candidate reassignment targets — same client, same portal-manager
  // tier, not the source itself, and still active. Cross-client transfers
  // are blocked backend-side too.
  const reassignTargets = useMemo(
    () => (allUsers || [])
      .filter((u) =>
        u.id !== user.id
        && (u.role === "regional_manager" || u.role === "site_manager")
        && u.client_id === user.client_id
        && u.is_active !== false,
      )
      .map((u) => ({
        value: u.id,
        label: `${u.name || u.email} · ${u.role.replace("_", " ")}`,
      })),
    [allUsers, user.id, user.client_id],
  );

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const r = await api.get(`/users/${user.id}/site-access-history?limit=25`);
      setHistory(r.data || []);
    } catch (e) {
      toast.error(errorMessage(e, "History failed"));
    } finally {
      setHistoryLoading(false);
    }
  }, [user.id]);

  // Fetch history lazily when the History tab is first opened for this
  // modal instance — avoids paying for the audit query on every picker use.
  useEffect(() => {
    if (open && tab === "history" && history.length === 0 && !historyLoading) {
      loadHistory();
    }
  }, [open, tab, history.length, historyLoading, loadHistory]);

  if (!isPortalManager) return null;

  const currentCount = (user.assigned_site_ids || []).length;

  const openModal = () => {
    setSelected(user.assigned_site_ids || []);
    setTab("edit");
    setReassignTargetId("");
    setReassignMerge(true);
    setReassignDeactivate(true);
    setOpen(true);
  };

  const save = async () => {
    if (selected.length === 0) {
      toast.error("Pick at least one site");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/users/${user.id}`, { assigned_site_ids: selected });
      toast.success(`Sites updated for ${user.name || user.email}`);
      setOpen(false);
      onChanged?.();
    } catch (e) {
      toast.error(errorMessage(e, "Update failed"));
    } finally {
      setBusy(false);
    }
  };

  const reassign = async () => {
    if (!reassignTargetId) {
      toast.error("Pick a target lead first");
      return;
    }
    if (currentCount === 0) {
      toast.error("This user has no sites to reassign");
      return;
    }
    const target = reassignTargets.find((t) => t.value === reassignTargetId);
    const confirmMsg = `Transfer ${currentCount} site${currentCount === 1 ? "" : "s"} from ${user.name || user.email} to ${target?.label || "the target"}?` +
      (reassignDeactivate ? "\n\nSource user will be DEACTIVATED (can no longer sign in)." : "") +
      (!reassignMerge ? "\n\nTarget's existing sites will be REPLACED, not merged." : "");
    if (!window.confirm(confirmMsg)) return;
    setBusy(true);
    try {
      const r = await api.post(`/users/${user.id}/reassign-sites`, {
        target_user_id: reassignTargetId,
        merge: reassignMerge,
        deactivate_source: reassignDeactivate,
      });
      const moved = r.data?.moved_site_ids?.length || 0;
      toast.success(`Moved ${moved} site${moved === 1 ? "" : "s"} to ${target?.label || "target"}${reassignDeactivate ? " · source deactivated" : ""}`);
      setOpen(false);
      onChanged?.();
    } catch (e) {
      toast.error(errorMessage(e, "Reassignment failed"));
    } finally {
      setBusy(false);
    }
  };

  if (!canWrite) {
    return (
      <PillBadge tone="default" data-testid={`manage-sites-static-${user.id}`}>
        {currentCount} site{currentCount === 1 ? "" : "s"}
      </PillBadge>
    );
  }

  return (
    <>
      <GhostButton
        data-testid={`manage-sites-${user.id}`}
        onClick={openModal}
        title={`Edit assigned sites (${currentCount})`}
      >
        <MapPin className="size-3.5" />
        <span className="text-[10px] font-mono ml-1">{currentCount}</span>
      </GhostButton>
      {open && (
        <Modal
          title={`Assigned sites — ${user.name || user.email}`}
          onClose={() => setOpen(false)}
          testId={`manage-sites-modal-${user.id}`}
        >
          <div className="space-y-4">
            {/* Header context line + tab strip */}
            <div className="text-xs text-zinc-400">
              This <span className="text-white font-semibold">{ROLE_LABEL[user.role] || user.role}</span>
              {" "}manages sites for <span className="text-white font-semibold">{user.client_name || "their client"}</span>.
              Currently assigned: <span className="text-white font-mono">{currentCount}</span>.
            </div>
            <div className="flex items-center gap-1 border-b border-[#27272A]" data-testid={`manage-sites-tabs-${user.id}`}>
              {[
                { id: "edit", label: "Edit sites", icon: MapPin },
                { id: "reassign", label: "Reassign", icon: ArrowRightLeft },
                { id: "history", label: "History", icon: History },
              ].map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setTab(id)}
                  data-testid={`manage-sites-tab-${id}-${user.id}`}
                  className={`px-3 py-2 text-xs uppercase tracking-widest flex items-center gap-2 transition-colors border-b-2 -mb-px ${
                    tab === id
                      ? "border-[#FF3B30] text-white"
                      : "border-transparent text-zinc-500 hover:text-zinc-300"
                  }`}
                >
                  <Icon className="size-3.5" /> {label}
                </button>
              ))}
            </div>

            {/* Tab: Edit sites */}
            {tab === "edit" && (
              <>
                {options.length === 0 ? (
                  <div className="border border-[#27272A] bg-[#0A0A0A] p-4 text-xs text-zinc-500 italic" data-testid="manage-sites-empty">
                    This client has no sites yet. Add a site under the <span className="text-[#FF3B30]">Sites</span> tab first.
                  </div>
                ) : (
                  <FormField label="Assigned Sites * (≥1)">
                    <MultiSelectDropdown
                      testId={`manage-sites-picker-${user.id}`}
                      label={`${options.length} sites available`}
                      options={options}
                      selected={selected}
                      onChange={setSelected}
                      searchable
                      triggerClassName="w-full"
                    />
                  </FormField>
                )}
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                  Regional / Site Managers only ever see the sites they&apos;re explicitly assigned to.
                </div>
                <div className="pt-2 flex items-center gap-3">
                  <ActionButton
                    onClick={save}
                    disabled={busy || options.length === 0 || selected.length === 0}
                    testid={`manage-sites-save-${user.id}`}
                  >
                    {busy ? "Saving…" : "Save"}
                  </ActionButton>
                  <GhostButton onClick={() => setOpen(false)} testid={`manage-sites-cancel-${user.id}`}>
                    Cancel
                  </GhostButton>
                </div>
              </>
            )}

            {/* Tab: Reassign */}
            {tab === "reassign" && (
              <div className="space-y-4" data-testid={`manage-sites-reassign-${user.id}`}>
                <div className="border border-[#FF9F0A]/30 bg-[#FF9F0A]/5 p-3 text-xs text-[#FF9F0A]">
                  <span className="font-semibold uppercase tracking-widest">Bulk transfer</span>
                  <div className="mt-1 text-[#FF9F0A]/80 leading-relaxed">
                    Move ALL {currentCount} of this user&apos;s sites to another Regional / Site Manager on the same client. Useful when someone leaves or the team reorganises. Audited on both sides.
                  </div>
                </div>
                {reassignTargets.length === 0 ? (
                  <div className="border border-[#27272A] bg-[#0A0A0A] p-4 text-xs text-zinc-500 italic" data-testid={`reassign-no-targets-${user.id}`}>
                    No other active Regional / Site Managers on this client. Create one first, or reassign the source&apos;s role to move the account itself.
                  </div>
                ) : (
                  <>
                    <FormField label="Transfer sites to">
                      <SingleSelectDropdown
                        testId={`reassign-target-${user.id}`}
                        label="Pick target lead"
                        options={reassignTargets}
                        value={reassignTargetId}
                        onChange={setReassignTargetId}
                        searchable
                        triggerClassName="w-full"
                      />
                    </FormField>
                    <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer" data-testid={`reassign-merge-${user.id}`}>
                      <input
                        type="checkbox"
                        checked={reassignMerge}
                        onChange={(e) => setReassignMerge(e.target.checked)}
                        className="accent-[#FF3B30]"
                      />
                      <span>Merge into target&apos;s existing sites (recommended). Unchecked = REPLACE target&apos;s list.</span>
                    </label>
                    <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer" data-testid={`reassign-deactivate-${user.id}`}>
                      <input
                        type="checkbox"
                        checked={reassignDeactivate}
                        onChange={(e) => setReassignDeactivate(e.target.checked)}
                        className="accent-[#FF3B30]"
                      />
                      <span>Deactivate this user afterwards (typical &quot;someone left&quot; flow).</span>
                    </label>
                    <div className="pt-2 flex items-center gap-3">
                      <ActionButton
                        onClick={reassign}
                        disabled={busy || !reassignTargetId || currentCount === 0}
                        testid={`reassign-submit-${user.id}`}
                      >
                        {busy ? "Transferring…" : `Transfer ${currentCount} site${currentCount === 1 ? "" : "s"}`}
                      </ActionButton>
                      <GhostButton onClick={() => setOpen(false)} testid={`reassign-cancel-${user.id}`}>
                        Cancel
                      </GhostButton>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Tab: History */}
            {tab === "history" && (
              <div className="space-y-3" data-testid={`manage-sites-history-${user.id}`}>
                <div className="text-xs text-zinc-500">
                  Every time this user&apos;s site scope changed — from the picker, an invite, or a bulk reassignment.
                </div>
                {historyLoading ? (
                  <div className="text-xs text-zinc-500 italic p-4 text-center">Loading history…</div>
                ) : history.length === 0 ? (
                  <div className="border border-[#27272A] bg-[#0A0A0A] p-4 text-xs text-zinc-500 italic" data-testid={`history-empty-${user.id}`}>
                    No site-access changes recorded yet. All future edits show up here.
                  </div>
                ) : (
                  <ul className="border border-[#27272A] divide-y divide-[#27272A] max-h-[320px] overflow-y-auto">
                    {history.map((ev) => (
                      <li key={ev.id} className="p-3 hover:bg-[#0E0E0E]" data-testid={`history-row-${ev.id}`}>
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500">
                          <span>{new Date(ev.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                          <span>·</span>
                          <span className="text-zinc-300 font-mono normal-case tracking-normal">{ev.user_email || "system"}</span>
                        </div>
                        {ev.summary && (
                          <div className="text-xs text-zinc-200 mt-1 break-words">{ev.summary}</div>
                        )}
                        {(ev.added_sites?.length > 0 || ev.removed_sites?.length > 0) && (
                          <div className="mt-1.5 flex flex-wrap gap-1.5">
                            {ev.added_sites?.map((s) => (
                              <span key={`a-${s.id}`} className="text-[10px] px-1.5 py-0.5 border border-[#34C759]/40 text-[#34C759] font-mono">
                                +{s.name}
                              </span>
                            ))}
                            {ev.removed_sites?.map((s) => (
                              <span key={`r-${s.id}`} className="text-[10px] px-1.5 py-0.5 border border-[#FF3B30]/40 text-[#FF3B30] font-mono line-through">
                                −{s.name}
                              </span>
                            ))}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </Modal>
      )}
    </>
  );
}


/**
 * RoleCell — inline role picker for a single user row.
 *
 * Options list built-in roles (from ROLE_LABELS) AND agency-scoped custom
 * roles (from /api/roles). Selecting a built-in clears `role_id`; selecting
 * a custom role sends `role_id` (backend keeps `role` in sync with base_role).
 *
 * Refuses to change the caller's own role — that's a lock-yourself-out
 * footgun, and the backend rejects it too as a second line of defence.
 */
function RoleCell({ user, currentUserId, roles, canWrite, onChanged }) {
  const isSelf = user.id === currentUserId;
  const options = useMemo(() => {
    const builtinSlugs = Object.keys(ROLE_LABEL);
    const builtinOpts = builtinSlugs.map((slug) => ({ value: `builtin:${slug}`, label: ROLE_LABEL[slug] || slug, group: "Built-in" }));
    const customOpts = (roles || [])
      .filter((r) => !r.is_builtin && r.is_active !== false)
      .map((r) => ({ value: `custom:${r.id}`, label: r.display_name, group: "Custom (agency)" }));
    return [...builtinOpts, ...customOpts];
  }, [roles]);

  const currentValue = user.role_id ? `custom:${user.role_id}` : `builtin:${user.role}`;

  const onChange = async (e) => {
    const v = e.target.value;
    try {
      if (v.startsWith("custom:")) {
        await api.patch(`/users/${user.id}`, { role_id: v.slice("custom:".length) });
      } else {
        await api.patch(`/users/${user.id}`, { role: v.slice("builtin:".length), role_id: "" });
      }
      toast.success("Role updated");
      onChanged?.();
    } catch (err) {
      toast.error(errorMessage(err, "Update failed"));
    }
  };

  if (!canWrite || isSelf) {
    // Non-writers + self see the classic pill badge (no self-edit footgun).
    const label = user.role_id
      ? (roles.find((r) => r.id === user.role_id)?.display_name || user.role)
      : (ROLE_LABEL[user.role] || user.role);
    return <PillBadge tone={roleTone(user.role)}>{label}</PillBadge>;
  }

  return (
    <select
      data-testid={`role-select-${user.id}`}
      className="bg-[#0A0A0A] border border-[#27272A] px-2 py-1 text-xs font-mono min-w-[10rem]"
      value={currentValue}
      onChange={onChange}
    >
      <optgroup label="Built-in">
        {options.filter((o) => o.group === "Built-in").map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </optgroup>
      {options.some((o) => o.group === "Custom (agency)") && (
        <optgroup label="Custom (agency)">
          {options.filter((o) => o.group === "Custom (agency)").map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </optgroup>
      )}
    </select>
  );
}
